import { isAbortError } from './abort-reasons.js';
import { restoreGlobeTerrain, globeTerrainState } from './globe-terrain.js';
import { ensureAzureSatelliteAccess } from './azure-satellite.js';
import { installGlobeSatelliteRecovery, isSatelliteAccessError } from './globe-satellite-recovery.js';
import { isSatelliteBasemap } from '../shared/satellite-access.js';
import { loadAzureAttribution, publishAzureAttribution, getAzureAttributionHtml } from './azure-attribution.js';
import { installGlobeModifierDrag } from './globe-modifier-drag.js';
import { mountGlobeMapControls } from './globe-map-controls.js';
import { observeGlobeCamera } from './globe-camera.js';
import { createLazyFeature } from './lazy-feature-loader.js';
import { t } from './i18n.js';
import { setGlobeProjection, createMapLibreProjection } from './globe-projection.js';
import { ol } from './ol-map.js';
import { getSatelliteViewMaxZoom, normalizeBasemapId } from './map-shared.js';
import { createGlobeDistanceCapability } from './globe-distance.js';
import { GLOBE_VECTOR_ATTRIBUTION, isGlobeBasemapLayer, loadGlobeBasemap, replaceGlobeBasemap } from './globe-basemap.js';
import { GlobeStrokes } from './globe-strokes.js';
import { GlobeSource, globeFeature } from './globe-source.js';
import { createGlobeLabelCanvas } from './globe-labels.js';
import type * as MapLibre from 'maplibre-gl';
import type { Map as OlMap } from 'ol';
import type { Point, Stroke } from '../types/app.js';

type GlobeSession = { status: 'off' }
  | { status: 'loading'; result: Promise<void>; viewer: MapLibre.Map | null }
  | { status: 'ready'; viewer: MapLibre.Map; drawings: GlobeStrokes; search: GlobeSource | null };
let session: GlobeSession = { status: 'off' };
const currentViewer = () => session.status === 'off' ? null : session.viewer;
let basemapGeneration = 0;
let viewerBasemap = 'carto';
const BUILDING_COLORS = {
  carto: '#cbd0d5',
  dark: '#344653',
  landscape: '#d2cec2',
  osm: '#d7c8bd',
  opentopo: '#c9c3b5',
  satellite: '#e0e4e8',
  hybrid: '#e0e4e8',
};

export function getGlobeResolutionScale(devicePixelRatio: number, maximumPhysicalScale = 2): number {
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  return Math.min(1, maximumPhysicalScale / dpr);
}
export function createGlobeViewerOptions(basemapId = 'carto') {
  return { attributionControl: false, renderWorldCopies: false, maxPitch: 60, minZoom: isSatelliteBasemap(basemapId) ? 0 : -1, maxZoom: isSatelliteBasemap(basemapId) ? getSatelliteViewMaxZoom() - 1 : 21, fadeDuration: 0, canvasContextAttributes: { preserveDrawingBuffer: true } } satisfies Partial<MapLibre.MapOptions>;
}
const moduleUrl = '/assets/maplibre/maplibre-gl.mjs';
const loadMapLibre = createLazyFeature<typeof MapLibre>(() => import(/* @vite-ignore */ moduleUrl), { automaticRecovery: false });
function syncBuildings(map: MapLibre.Map): void {
  const tilted = map.getPitch() > 0;
  if (tilted && !map.getSource('openfreemap-buildings')) {
    map.addSource('openfreemap-buildings', {
      type: 'vector', url: 'https://tiles.openfreemap.org/planet',
      attribution: GLOBE_VECTOR_ATTRIBUTION,
    });
    map.addLayer({
      id: '3d-buildings', type: 'fill-extrusion', source: 'openfreemap-buildings',
      'source-layer': 'building', minzoom: 14,
      filter: ['!=', ['get', 'hide_3d'], true],
      paint: {
        'fill-extrusion-color': BUILDING_COLORS[normalizeBasemapId(viewerBasemap)],
        'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 0],
        'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
        'fill-extrusion-opacity': 1,
      },
    }, map.getStyle().layers.find(layer => layer.type === 'circle' || layer.type === 'symbol')?.id);
    for (const layer of map.getStyle().layers) {
      if (!isGlobeBasemapLayer(layer.id) && (layer.type === 'fill' || layer.type === 'line')) map.moveLayer(layer.id, '3d-buildings');
    }
  }
  if (map.getLayer('3d-buildings')) map.setLayoutProperty('3d-buildings', 'visibility', tilted ? 'visible' : 'none');
}
const globeDistance = createGlobeDistanceCapability({ getViewer: currentViewer });
function setGlobePresentation(active: boolean): void {
  const mapEl = document.getElementById('map');
  const container = document.getElementById('globe-container');
  if (container) container.style.zIndex = '0';
  mapEl?.classList.toggle('globe-active', active);
  document.body?.classList.toggle('globe-active', active);
  // SAFETY: The application template owns this selector and supplies the stated DOM element type.
  const olViewport = mapEl?.querySelector('.ol-viewport') as HTMLElement | null;
  if (olViewport) {
    olViewport.style.zIndex = '1';
    const layers = olViewport.querySelector<HTMLElement>('.ol-layers');
    if (layers) layers.style.visibility = active ? 'hidden' : '';
    olViewport.style.pointerEvents = active ? 'none' : 'auto';
  }
  const drawingCanvas = document.getElementById('drawing-canvas');
  if (drawingCanvas) {
    drawingCanvas.style.zIndex = '999';
    drawingCanvas.style.pointerEvents = 'none';
  }
}

export function enableGlobeView(olMap: OlMap, strokes: Stroke[], currentBasemap: string): Promise<void> {
  if (session.status === 'ready') return Promise.resolve();
  if (session.status === 'loading') return session.result;
  const result = Promise.resolve().then(() => createGlobeView(olMap, strokes, currentBasemap, loading)).catch(error => {
    if (session === loading || loading.viewer && currentViewer() === loading.viewer) destroyGlobeView();
    throw error;
  });
  const loading: Extract<GlobeSession, { status: 'loading' }> = { status: 'loading', result, viewer: null };
  session = loading;
  return result;
}

async function createGlobeView(olMap: OlMap, strokes: Stroke[], currentBasemap: string, attempt: Extract<GlobeSession, { status: 'loading' }>): Promise<void> {
  if (!document.querySelector('link[data-globe-style]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = '/assets/maplibre/maplibre-gl.css'; link.dataset.globeStyle = '';
    document.head.appendChild(link);
  }
  const library = await loadMapLibre();
  if (isSatelliteBasemap(currentBasemap)) await ensureAzureSatelliteAccess();
  const basemapStyle = await loadGlobeBasemap(currentBasemap);
  if (session !== attempt) throw new DOMException('Globe loading was cancelled', 'AbortError');
  const container = document.createElement('div');
  container.id = 'globe-container';
    container.className = 'globe-container base:[#globe-container:where(&)]:absolute base:[#globe-container:where(&)]:top-[0] base:[#globe-container:where(&)]:left-[0] base:[#globe-container:where(&)]:w-[100%] base:[#globe-container:where(&)]:h-[100%] base:[#globe-container:where(&)]:z-[var(--layer-map)]';
  document.getElementById('map')!.appendChild(container);
  try {
    const view = olMap.getView();
    const center = ol.proj.toLonLat(view.getCenter() ?? [0, 0]);
    const ratio = window.devicePixelRatio || 1;
    const map = new library.Map({ ...createGlobeViewerOptions(currentBasemap), container, style: { ...basemapStyle, light: { anchor: 'viewport', position: [1.5, 315, 45], color: '#ffffff', intensity: 0.65 } }, center: [center[0], center[1]], zoom: (view.getZoom() ?? 2) - 1, pixelRatio: ratio * getGlobeResolutionScale(ratio) });
    installGlobeSatelliteRecovery(map);
    attempt.viewer = map;
    viewerBasemap = currentBasemap;
    let creditControl = new library.AttributionControl({ compact: true });
    map.addControl(creditControl);
    let lastCredits = '';
    const setCredits = (credits: string) => {
      if (credits === lastCredits) return;
      lastCredits = credits;
      map.removeControl(creditControl);
      creditControl = new library.AttributionControl({ compact: true, customAttribution: credits });
      map.addControl(creditControl);
    };
    let creditVersion = 0;
    const updateCredits = async () => {
      const version = ++creditVersion;
      // MapLibre copies event listeners before dispatch. Replacing a control
      // during styledata leaves its removed listener in that dispatch.
      await Promise.resolve();
      if (currentViewer() !== map || version !== creditVersion) return;
      if (!isSatelliteBasemap(viewerBasemap)) { setCredits(''); return; }
      const bounds = map.getBounds();
      try {
        const credits = await loadAzureAttribution([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()], map.getZoom() + 1, viewerBasemap === 'hybrid');
        if (currentViewer() !== map || version !== creditVersion || !isSatelliteBasemap(viewerBasemap)) return;
        publishAzureAttribution(credits);
        setCredits(getAzureAttributionHtml());
      } catch { /* Keep the last credits while the next map request retries. */ }
    };
    map.on('moveend', updateCredits);
    map.on('styledata', updateCredits);
    void updateCredits();
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => finish(new Error(t('globe.load_failed'))), 30000);
      const finish = (error?: Error) => { clearTimeout(timer); map.off('load', loaded); map.off('error', failed); map.off('remove', removed); if (error) reject(error); else resolve(); };
      const loaded = () => finish();
      const failed = (event: MapLibre.ErrorEvent) => { if (!isSatelliteAccessError(event.error)) finish(new Error(event.error.message)); };
      const removed = () => finish(new DOMException('Globe loading was cancelled', 'AbortError'));
      map.on('load', loaded); map.on('error', failed); map.on('remove', removed);
    });
    if (session !== attempt) throw new DOMException('Globe loading was cancelled', 'AbortError');
    const drawings = new GlobeStrokes(map);
    session = { status: 'ready', viewer: map, drawings, search: null };
    setGlobePresentation(true);
    map.resize();
    setGlobeProjection(createMapLibreProjection(map));
    observeGlobeCamera(map);
    mountGlobeMapControls(map, library);
    installGlobeModifierDrag(map);
    drawings.sync(strokes, null);
    map.on('pitch', () => syncBuildings(map));
    map.on('move', () => window.dispatchEvent(new CustomEvent('globe-camera-changed')));
    map.getCanvas().addEventListener('webglcontextlost', handleGlobeContextLost);
    restoreGlobeTerrain(map);
    window.dispatchEvent(new CustomEvent('globe-view-changed'));
  } catch (cause) {
    if (session === attempt) destroyGlobeView();
    if (isAbortError(cause)) throw cause;
    throw new Error(cause instanceof Error && /webgl|gpu/i.test(cause.message) ? t('globe.unsupported') : t('globe.load_failed'), { cause });
  }
}
function handleGlobeContextLost(): void {
  destroyGlobeView();
  window.dispatchEvent(new CustomEvent('globe-render-error', { detail: { message: t('globe.stopped_unexpectedly') } }));
}
export function destroyGlobeView(): void {
  if (session.status === 'off') return;
  basemapGeneration++;
  globeDistance.destroy();
  setGlobeProjection(null);
  setGlobePresentation(false);
  const previous = currentViewer();
  session = { status: 'off' };
  if (document.body && document.documentElement) window.dispatchEvent(new CustomEvent('globe-view-changed'));
  previous?.getCanvas().removeEventListener('webglcontextlost', handleGlobeContextLost);
  previous?.remove();
  document.getElementById('globe-container')?.remove();
}
export function disableGlobeView(olMap: OlMap): void {
  const snapshot = getGlobeViewportSnapshot();
  if (snapshot) { olMap.getView().setCenter(ol.proj.fromLonLat([snapshot.center.lng, snapshot.center.lat])); olMap.getView().setZoom(snapshot.zoom); }
  destroyGlobeView();
  olMap.updateSize();
}
export function refreshGlobeStrokes(strokes: Stroke[], pendingEraseIds: Set<string> | null = null): void { if (session.status === 'ready') session.drawings.sync(strokes, pendingEraseIds); }
export function getGlobeViewer(): MapLibre.Map | null { return session.status === 'ready' ? session.viewer : null; }
export function setGlobeNavigationEnabled(enabled: boolean): void {
  const viewer = currentViewer();
  if (!viewer) return;
  for (const control of [viewer.dragPan, viewer.dragRotate, viewer.keyboard, viewer.touchZoomRotate, viewer.touchPitch]) {
    if (enabled) control.enable(); else control.disable();
  }
  viewer.touchZoomRotate.disableRotation();
}
export function isGlobeEnabled(): boolean { return session.status === 'ready'; }
export function setGlobeBasemap(basemapId: string): void {
  const map = currentViewer();
  if (!map) return;
  const attempt = ++basemapGeneration;
  const access = isSatelliteBasemap(basemapId) ? ensureAzureSatelliteAccess() : Promise.resolve();
  void access.then(() => loadGlobeBasemap(basemapId)).then(style => {
    if (currentViewer() === map && attempt === basemapGeneration) {
      replaceGlobeBasemap(map, style);
      viewerBasemap = basemapId;
      const limits = createGlobeViewerOptions(basemapId);
      map.setMinZoom(limits.minZoom);
      map.setMaxZoom(limits.maxZoom);
      if (map.getLayer('3d-buildings')) map.setPaintProperty('3d-buildings', 'fill-extrusion-color', BUILDING_COLORS[normalizeBasemapId(basemapId)]);
    }
  }).catch(() => {
    if (currentViewer() === map && attempt === basemapGeneration) {
      window.dispatchEvent(new CustomEvent('globe-basemap-error'));
    }
  });
}
export function setGlobeCamera(camera: { pitch: number; bearing: number }): void {
  currentViewer()?.jumpTo(camera);
}
export function getGlobeViewportSnapshot(): { center: Point; zoom: number; camera: { pitch: number; bearing: number; terrain: boolean } } | null {
  const viewer = currentViewer();
  if (!viewer) return null;
  const center = viewer.getCenter();
  return { center: { lat: center.lat, lng: center.lng }, zoom: viewer.getZoom() + 1, camera: { pitch: viewer.getPitch(), bearing: viewer.getBearing(), terrain: globeTerrainState(viewer).active } };
}
export function flyGlobeTo(lat: number, lng: number): void { const viewer = currentViewer(); viewer?.flyTo({ center: [lng, lat], zoom: Math.max(viewer.getZoom(), 9), duration: 1500 }); }
export function showGlobeSearchResult(lat: number, lng: number, label: string): void {
  if (session.status !== 'ready') return;
  const { viewer } = session;
  const search = session.search ??= new GlobeSource(viewer, 'search');
  const geometry = { type: 'Point' as const, coordinates: [lng, lat] };
  const features = [globeFeature('search', geometry, { color: '#2563eb' })];
  if (label) {
    const image = createGlobeLabelCanvas(label);
    features.push(globeFeature('search-label', geometry, { image: search.image('label', image.image, image.pixelRatio), offset: image.offset }));
  }
  search.set(features);
  flyGlobeTo(lat, lng);
}
export function getGlobeDegreesPerPixel(): number { const viewer = currentViewer(); return viewer ? 360 / (512 * 2 ** viewer.getZoom()) : 0.01; }
export const syncGlobeDistanceWaypoints = globeDistance.syncWaypoints;
export const enableGlobeDistanceInteraction = globeDistance.enableInteraction;
export const disableGlobeDistanceInteraction = globeDistance.disableInteraction;
